import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import Select from 'react-select';
import { FaExchangeAlt } from 'react-icons/fa';
import Breadcrumb from '../components/Breadcrumbs/Breadcrumb';
import DefaultLayout from '../layout/DefaultLayout';

interface CryptoInfo {
  name: string;
  image: { large: string };
  market_data: {
    market_cap?: { usd?: number };
    total_volume?: { usd?: number };
    circulating_supply?: number;
  };
  description: { en: string };
}

const CryptoConverter: React.FC = () => {
  const [crypto, setCrypto] = useState({ value: 'bitcoin', label: 'Bitcoin' });
  const [fiat, setFiat] = useState({ value: 'usd', label: 'USD' });
  const [cryptoAmount, setCryptoAmount] = useState<number>(1);
  const [fiatAmount, setFiatAmount] = useState<number>(0);
  const [cryptoInfo, setCryptoInfo] = useState<Partial<CryptoInfo>>({});
  const [cryptoOptions, setCryptoOptions] = useState<{ value: string; label: string }[]>([]);
  const fiatOptions = [
    { value: 'usd', label: 'USD' },
    { value: 'eur', label: 'EUR' },
    { value: 'gbp', label: 'GBP' },
    { value: 'zar', label: 'ZAR' },
  ];

  useEffect(() => {
    fetchCryptocurrencies();
  }, []);

  useEffect(() => {
    fetchConversionRate();
    fetchCryptoInfo();
  }, [crypto, fiat, cryptoAmount]);

  const fetchCryptocurrencies = async () => {
    try {
      const res = await axios.get('https://api.coingecko.com/api/v3/coins/markets', {
        params: {
          vs_currency: 'usd',
          order: 'market_cap_desc',
          per_page: 200,
        },
      });
      setCryptoOptions(res.data.map((coin: any) => ({ value: coin.id, label: coin.name })));
    } catch (err) {
      console.error('Error fetching cryptocurrencies:', err);
    }
  };

  const fetchConversionRate = async () => {
    try {
      const res = await axios.get(`https://api.coingecko.com/api/v3/simple/price?ids=${crypto.value}&vs_currencies=${fiat.value}`);
      const price = res.data[crypto.value]?.[fiat.value] || 0;
      setFiatAmount(price * cryptoAmount);
    } catch (err) {
      console.error('Error fetching rate:', err);
    }
  };

  const fetchCryptoInfo = async () => {
    try {
      const res = await axios.get(`https://api.coingecko.com/api/v3/coins/${crypto.value}`);
      setCryptoInfo(res.data);
    } catch (err) {
      console.error('Error fetching crypto info:', err);
    }
  };

  const formatCurrency = (amount: number, currency: string) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  const formatNumber = (value: number) =>
    value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleExchange = () => {
    setCrypto(fiat);
    setFiat(crypto);
    setCryptoAmount(fiatAmount);
    setFiatAmount(cryptoAmount);
  };

  return (
    <DefaultLayout>
      <Breadcrumb pageName="Crypto Converter" />
      <div className="bg-white/10 border border-white/10 rounded-xl shadow-lg p-6 md:p-10 backdrop-blur-md">
        <h2 className="text-2xl font-bold text-white mb-6">Convert Crypto to Fiat</h2>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-white font-semibold mb-2">Cryptocurrency</label>
            <Select
              value={crypto}
              onChange={(val) => val && setCrypto(val)}
              options={cryptoOptions}
              className="text-black"
            />
          </div>
          <div>
            <label className="block text-white font-semibold mb-2">Fiat Currency</label>
            <Select
              value={fiat}
              onChange={(val) => val && setFiat(val)}
              options={fiatOptions}
              className="text-black"
            />
          </div>
        </div>

        <div className="flex items-end gap-4 mb-6">
          <div className="w-full">
            <label className="block text-white font-semibold mb-2">
              Amount of {crypto.label}
            </label>
            <input
              type="number"
              value={cryptoAmount}
              onChange={(e) => setCryptoAmount(Number(e.target.value))}
              className="w-full px-4 py-2 rounded-md bg-white text-black border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <button
            onClick={handleExchange}
            className="p-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition"
            title="Swap"
          >
            <FaExchangeAlt className="text-xl" />
          </button>
        </div>

        <div className="bg-white/5 p-4 rounded-md text-white font-medium text-lg mb-6">
          Equivalent: <span className="font-bold">{formatCurrency(fiatAmount, fiat.value.toUpperCase())}</span>
        </div>

        <Link
          to={`/dashboard/tables`}
          className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 transition"
        >
          Buy {crypto.label}
        </Link>

        {cryptoInfo.name && (
          <div className="mt-10 bg-white/5 p-6 rounded-xl text-white">
            <div className="flex flex-col items-center gap-4">
              <img src={cryptoInfo.image?.large} alt={cryptoInfo.name} className="w-16 h-16 rounded-full shadow" />
              <h3 className="text-xl font-bold">{cryptoInfo.name}</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-6 text-sm">
              <p><strong>Market Cap:</strong> {formatCurrency(cryptoInfo.market_data?.market_cap?.usd || 0, 'USD')}</p>
              <p><strong>24h Volume:</strong> {formatCurrency(cryptoInfo.market_data?.total_volume?.usd || 0, 'USD')}</p>
              <p><strong>Circulating Supply:</strong> {formatNumber(cryptoInfo.market_data?.circulating_supply || 0)}</p>
            </div>
            <div
              className="prose prose-invert prose-sm mt-4 max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cryptoInfo.description?.en || '') }}
            />
          </div>
        )}
      </div>
    </DefaultLayout>
  );
};

export default CryptoConverter;







